export type Locale = 'zh' | 'en';

export const messages = {
  common: {
    signIn: { zh: '使用 Microsoft 登入', en: 'Sign in with Microsoft' },
    signInHint: {
      zh: '請使用 Microsoft 365 公司帳號登入',
      en: 'Sign in with your Microsoft 365 work account',
    },
    signOut: { zh: '登出', en: 'Sign out' },
    cancel: { zh: '取消', en: 'Cancel' },
    create: { zh: '建立', en: 'Create' },
    add: { zh: '新增', en: 'Add' },
    delete: { zh: '刪除', en: 'Delete' },
    edit: { zh: '編輯', en: 'Edit' },
    enabled: { zh: '啟用', en: 'Enabled' },
    copy: { zh: '複製', en: 'Copy' },
    copied: { zh: '已複製到剪貼簿', en: 'Copied to clipboard' },
    close: { zh: '關閉', en: 'Close' },
    all: { zh: '全部', en: 'All' },
    never: { zh: '—', en: '—' },
    label: { zh: '標籤', en: 'Label' },
    priority: { zh: '優先權', en: 'Priority' },
    lastUsed: { zh: '最後使用', en: 'Last used' },
    createdAt: { zh: '建立時間', en: 'Created' },
    actions: { zh: '動作', en: 'Actions' },
    darkMode: { zh: '深色模式', en: 'Dark mode' },
    language: { zh: '語言', en: 'Language' },
    confirmDeleteTitle: { zh: '確認刪除', en: 'Confirm delete' },
    createFailed: { zh: '建立失敗', en: 'Create failed' },
    saveFailed: { zh: '儲存失敗', en: 'Save failed' },
    role: { zh: '角色', en: 'Role' },
    status: { zh: '狀態', en: 'Status' },
    email: { zh: 'Email', en: 'Email' },
    name: { zh: '名稱', en: 'Name' },
    approve: { zh: '核准', en: 'Approve' },
    deny: { zh: '拒絕', en: 'Deny' },
    retry: { zh: '重試', en: 'Retry' },
  },
  nav: {
    dashboard: { zh: 'Dashboard', en: 'Dashboard' },
    sharedKeys: { zh: '分享 Key', en: 'Shared keys' },
    parentKeys: { zh: '母 Key', en: 'Parent keys' },
    logs: { zh: 'Logs', en: 'Logs' },
    users: { zh: '成員', en: 'Users' },
  },
  dashboard: {
    reqsPerSec: { zh: '最近一秒 requests', en: 'Requests last second' },
    avgLatency: { zh: '上游平均延遲', en: 'Avg upstream latency' },
    p95Latency: { zh: 'p95 延遲', en: 'p95 latency' },
    errorRate: { zh: '錯誤率（最近一秒）', en: 'Error rate (last second)' },
    activeKeys: { zh: '活躍分享 Key 數', en: 'Active shared keys' },
    liveTraffic: { zh: '即時流量（5 分鐘）', en: 'Live traffic (5 min)' },
    perKeyStack: { zh: '每把 Key req/s 堆疊', en: 'Per-key req/s stack' },
    refresh1s: { zh: '每秒 1 次更新', en: 'Updated every 1 s' },
    legendTotal: { zh: '總 req/s', en: 'Total req/s' },
    legendErrors: { zh: 'errors/s', en: 'errors/s' },
  },
  sharedKeys: {
    title: { zh: '分享 Key', en: 'Shared keys' },
    accessKey: { zh: 'Access Key', en: 'Access Key' },
    parentKey: { zh: '母 Key', en: 'Parent key' },
    maxRps: { zh: 'max RPS', en: 'max RPS' },
    minInterval: { zh: 'min interval', en: 'min interval' },
    adAccounts: { zh: '可用 Ad Account', en: 'Ad accounts' },
    adAccountsAny: { zh: '全部', en: 'any' },
    adAccountsHelp: {
      zh: '允許使用的 Ad Account ID（每行一個，空白代表不限制）',
      en: 'Allowed Ad Account IDs (one per line; blank = unrestricted)',
    },
    adAccountsHint: {
      zh: 'Proxy 會從路徑 /api/v3/adaccounts/{id}/... 擷取 id 做白名單檢查；不在名單回 403。',
      en: 'Proxy pulls the id out of /api/v3/adaccounts/{id}/… and rejects anything not on this list with 403.',
    },
    createTitle: { zh: '新增分享 Key', en: 'Create shared key' },
    editTitle: { zh: '編輯分享 Key', en: 'Edit shared key' },
    save: { zh: '儲存', en: 'Save' },
    labelHelp: { zh: '標籤（給團隊看的）', en: 'Label (internal)' },
    parentHelp: { zh: '對應的母 Key', en: 'Parent key' },
    maxRpsHelp: { zh: '每秒最多次數 (max RPS)', en: 'Max RPS' },
    minIntervalHelp: {
      zh: '最小間隔毫秒 (0 = 不限制)',
      en: 'Min interval ms (0 = none)',
    },
    priorityHelp: {
      zh: '優先權 1-10（越高越先搶到 slot）',
      en: 'Priority 1–10 (higher wins slot)',
    },
    secretTitle: { zh: '分享 Key 憑證', en: 'Shared key credentials' },
    secretBody: {
      zh: '複製下列 Access Key / Secret Key 給使用者。相容 LINE Ads API 簽章，使用者把 base URL 改成 gateway 即可直接跑原本的程式碼。',
      en: 'Copy the Access Key / Secret Key below for the consumer. They are LINE Ads API-compatible — consumers only need to swap the base URL to use the gateway.',
    },
    regenerate: { zh: '重建 secret', en: 'Regenerate secret' },
    secretInfoTitle: { zh: 'Secret 資訊', en: 'Secret info' },
    secretInfoNote: {
      zh: '原始 Secret Key 不會存入資料庫。若遺失請按「重建」產生新的，舊 Secret Key 會立即失效。',
      en: 'The raw Secret Key is not stored. Regenerate to issue a new one — the old key stops working immediately.',
    },
    confirmDelete: { zh: '刪除 {label}？', en: 'Delete {label}?' },
  },
  parentKeys: {
    title: {
      zh: '母 Key（公司 LINE Ads API 憑證）',
      en: 'Parent keys (company LINE Ads API credentials)',
    },
    accessKey: { zh: 'Access Key', en: 'Access key' },
    maxRps: { zh: 'max RPS', en: 'max RPS' },
    createTitle: { zh: '新增母 Key', en: 'Create parent key' },
    editTitle: { zh: '編輯母 Key', en: 'Edit parent key' },
    save: { zh: '儲存', en: 'Save' },
    labelHelp: {
      zh: '標籤（例如：行銷部、廣告帳號 A1）',
      en: 'Label (e.g. Marketing, AdAccount A1)',
    },
    accessKeyHelp: {
      zh: 'LINE Ads API Access Key',
      en: 'LINE Ads API Access Key',
    },
    secretKeyHelp: {
      zh: 'LINE Ads API Secret Key（將以 AES-256-GCM 加密儲存）',
      en: 'LINE Ads API Secret Key (stored AES-256-GCM encrypted)',
    },
    maxRpsHelp: {
      zh: '上游每秒最多次數（此母 Key 下所有分享 Key 總和上限，LINE 每個帳號硬上限通常 10/s）',
      en: 'Max upstream RPS (aggregate cap across all shared keys under this parent; LINE per-account hard limit is ~10/s)',
    },
    confirmDelete: {
      zh: '刪除 {label}？會連帶刪除所有指向它的分享 Key。',
      en: 'Delete {label}? All shared keys pointing at it will cascade.',
    },
  },
  users: {
    title: { zh: '成員管理', en: 'Users' },
    subtitle: {
      zh: '管理誰能登入這個系統。新登入者需要由管理員核准後才能使用。',
      en: 'Control who can use this system. New sign-ins need an admin to approve them before they can access anything.',
    },
    lastLogin: { zh: '最後登入', en: 'Last sign-in' },
    statusPending: { zh: '待審核', en: 'Pending' },
    statusActive: { zh: '已核准', en: 'Active' },
    statusDenied: { zh: '已拒絕', en: 'Denied' },
    roleAdmin: { zh: '管理員', en: 'Admin' },
    roleViewer: { zh: '一般', en: 'Viewer' },
    makeAdmin: { zh: '設為管理員', en: 'Make admin' },
    makeViewer: { zh: '改為一般', en: 'Make viewer' },
    confirmDelete: {
      zh: '刪除 {email}？下次他登入時會以新成員身分重新進入待審核狀態。',
      en: 'Delete {email}? If they sign in again they will re-enter the pending queue.',
    },
    pendingTitle: { zh: '等待管理員核准', en: 'Waiting for admin approval' },
    pendingBody: {
      zh: '你的 Microsoft 帳號（{email}）已登入成功，但還沒被加入可使用這個系統的名單。請按下方按鈕向管理員送出申請，管理員核准後就能進入主畫面。',
      en: 'You are signed in as {email}, but your account has not been added to this system yet. Send a request below and an admin will review it.',
    },
    deniedTitle: { zh: '此帳號已被拒絕', en: 'Your account has been denied' },
    deniedBody: {
      zh: '你的帳號（{email}）被管理員拒絕使用此系統。如有疑問請聯絡管理員。',
      en: 'Your account ({email}) has been denied access to this system. Please contact an administrator if you believe this is a mistake.',
    },
    sendRequest: { zh: '送出申請', en: 'Send request' },
    requestSent: {
      zh: '已送出申請，請稍候管理員核准。',
      en: 'Request sent. Please wait for an admin to approve your account.',
    },
    emptyState: { zh: '目前沒有待審核的成員', en: 'No users yet' },
  },
  logs: {
    title: { zh: 'Logs（近 7 天）', en: 'Logs (last 7 days)' },
    autoRefresh: { zh: '每 5 秒自動刷新', en: 'Auto-refresh every 5 s' },
    sharedKey: { zh: '分享 Key', en: 'Shared key' },
    pathContains: { zh: 'Path 包含', en: 'Path contains' },
    errorsOnly: { zh: '只看錯誤', en: 'Errors only' },
    time: { zh: '時間', en: 'Time' },
    method: { zh: 'Method', en: 'Method' },
    path: { zh: 'Path', en: 'Path' },
    status: { zh: 'Status', en: 'Status' },
    latency: { zh: '延遲 (ms)', en: 'Latency (ms)' },
    queueWait: { zh: '佇列 (ms)', en: 'Queue wait (ms)' },
    errorReason: { zh: '錯誤原因', en: 'Error reason' },
  },
} as const;

type MessageMap = typeof messages;
type Section = keyof MessageMap;
type Key<S extends Section> = keyof MessageMap[S];

export function t<S extends Section, K extends Key<S>>(
  section: S,
  key: K,
  locale: Locale,
  replacements?: Record<string, string>
): string {
  const entry = messages[section][key] as { zh: string; en: string };
  let out = entry[locale];
  if (replacements) {
    for (const [k, v] of Object.entries(replacements)) {
      out = out.replaceAll(`{${k}}`, v);
    }
  }
  return out;
}
